import { Sparkles, TriangleAlert } from 'lucide-react';

import { cn, Label, StatusChip } from '@src/components/ui';


/**
 * The shared base-field shell: a vertical stack of an optional label-above, the
 * control, and the reserved helper/error line below. Every base field
 * (TextField / SelectField / AutocompleteField / SuggestField / CheckboxField)
 * renders its control as `children` here so label placement and error styling
 * live in one place.
 *
 * `id` wires the label to the control; omit `label` for controls that carry
 * their own inline label (CheckboxField).
 *
 * Field states (DESIGN.md `field-label` / `field-suggested` / `field-error`):
 *   - label      — 12px ink-subtle ABOVE the field, never placeholder-as-label.
 *   - suggested  — advisory prefill (VIN decode / lookup / interchange) the
 *                  seller has not confirmed yet. Renders the .pen "Suggested"
 *                  pill (`QoGzS`: sparkles + label) beside the label; the caller
 *                  drops it once the value diverges or is confirmed. Replaces the
 *                  ad-hoc `SuggestedLabel` the donor form passed as JSX in the
 *                  `label` prop.
 *                  PARTIAL, DELIBERATELY (AUTOORBIT-233 review): DESIGN.md:132
 *                  also specifies a DASHED primary-on-dark border on the control
 *                  itself, which only ships on the chip. Painting the control
 *                  needs a `suggested` variant on `fieldFrame` threaded through
 *                  Input / Select / Autocomplete — a wider change than this
 *                  ticket's delta, and the shell cannot reach the control it
 *                  renders as `children`. Tracked, not forgotten.
 *   - error      — danger border on the control (the field passes `invalid`) plus
 *                  an ICON + text message here. DESIGN.md `field-error` is
 *                  explicit that it is "icon + text, never color alone"; this
 *                  line used to be color-only.
 */
function FieldShell(props) {
  const {
    label, id, error = false, suggested = false, helperText = ' ', className, children,
  } = props;

  // The default helperText is a single space, which reserves the line's height —
  // do not hang an alert icon off that placeholder.
  const hasMessage = typeof helperText === 'string' ? helperText.trim() !== '' : Boolean(helperText);

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label
        ? (
          <span className="flex items-center gap-1.5">
            <Label htmlFor={id}>{label}</Label>

            {suggested
              ? (
                <StatusChip variant="suggested" className="gap-1 px-1.5 py-0.5 text-[10.5px]">
                  <Sparkles />
                  Suggested
                </StatusChip>
              )
              : null}
          </span>
        )
        : null}

      {children}

      <p className={cn('flex min-h-4 items-center gap-1 text-xs', error ? 'text-danger' : 'text-ink-subtle')}>
        {error && hasMessage
          ? <TriangleAlert className="size-3 shrink-0" />
          : null}

        {helperText}
      </p>
    </div>
  );
}

FieldShell.displayName = 'FieldShell';
export default FieldShell;
